import { useState } from 'react';
import SectionCard from './SectionCard';
import { MemberInline } from '../memberDisplay';

// Маленькая ячейка статистики в раскрытой строке
const StatCell = ({ label, value, accent }) => (
  <div style={{
    flex: 1, minWidth: 0, textAlign: 'center',
    padding: '6px 2px', borderRadius: 8,
    background: accent ? '#fff7ed' : '#f9fafb',
    border: accent ? '1px solid #fed7aa' : '1px solid #f0f0f5',
  }}>
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
      textTransform: 'uppercase', color: accent ? '#c2410c' : '#9ca3af',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </div>
    <div style={{
      fontSize: 14, fontWeight: 700, marginTop: 2,
      color: accent ? '#b45309' : '#111827', whiteSpace: 'nowrap',
    }}>
      {value}
    </div>
  </div>
);

const fmtDiff = (d) => {
  const n = d ?? 0;
  return n > 0 ? `+${n}` : `${n}`;
};

// Inline-подсказка тай-брейка. Бэк (StandingsCalculator#annotateTieBreaks) присылает
// `tieBreakMetric` только для строк-коллизий: когда главное (отображаемое) число совпадает
// с соседом, но место разное. Показываем метрику, по которой разошлось, и её значение —
// чтобы не было вопроса «почему очки одинаковые, а места разные», и без лишнего клика.
const tieBreakHint = (p) => {
  switch (p.tieBreakMetric) {
    case 'POINT_DIFF':    return `разн. ${fmtDiff(p.pointDiff)}`;
    case 'WINS':          return `побед ${p.wins ?? 0}`;
    case 'LOSSES':        return `пораж. ${p.losses ?? 0}`;
    case 'POINTS':        return `очки ${p.totalPoints ?? 0}`;
    case 'LEAGUE_POINTS': return `тур.очки ${p.leaguePoints ?? 0}`;
    default:              return null;
  }
};

// Цвета ярусных секций этапа 2 Beat the Box (ключ = groupLabel с бэка)
const BTB_TIER_COLORS = {
  Gold:    { bg: '#fef9c3', border: '#fde047', color: '#854d0e', badge: '#fef08a' },
  Silver:  { bg: '#f8fafc', border: '#cbd5e1', color: '#475569', badge: '#f1f5f9' },
  Bronze:  { bg: '#fff7ed', border: '#fed7aa', color: '#92400e', badge: '#ffedd5' },
  Crystal: { bg: '#f0f9ff', border: '#bae6fd', color: '#075985', badge: '#e0f2fe' },
};

// Палитра секций-боксов этапа 1 (лейблы «Бокс 1/2/3…» — назначаем цвет по индексу секции)
const BTB_BOX_COLORS = [
  { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', badge: '#dbeafe' },
  { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534', badge: '#dcfce7' },
  { bg: '#fef2f2', border: '#fecaca', color: '#991b1b', badge: '#fee2e2' },
  { bg: '#faf5ff', border: '#e9d5ff', color: '#6b21a8', badge: '#f3e8ff' },
  { bg: '#fffbeb', border: '#fde68a', color: '#92400e', badge: '#fef3c7' },
  { bg: '#f0fdfa', border: '#99f6e4', color: '#115e59', badge: '#ccfbf1' },
];

/**
 * Таблица лидеров.
 *
 * Для Beat the Box бэк присылает секции через `groupLabel`:
 *   • этап 1 — боксы «Бокс 1/2/3…» (места считаются внутри бокса);
 *   • этап 2 — ярусы Gold/Silver/Bronze/Crystal (место №1 Gold = победитель).
 * Порядок секций берём как пришёл с бэка (первое появление лейбла), а не из
 * жёсткой константы — тогда работают и боксы, и ярусы, и любое их число.
 *
 * @param {string}  [title]           заголовок карточки
 * @param {boolean} [collapsible]     показать кнопку сворачивания всей карточки
 * @param {boolean} [defaultCollapsed] стартовое состояние (для истории этапа 1)
 */
const Leaderboard = ({
  players,
  rankingMode = 'POINTS',
  hasCompletedRound = false,
  title = 'Таблица лидеров',
  collapsible = false,
  defaultCollapsed = false,
}) => {
  // Набор раскрытых строк. Тап по строке переключает её; кнопка в шапке —
  // раскрывает/сворачивает все сразу (то же состояние).
  const [openIds, setOpenIds] = useState(() => new Set());
  // Свёрнута ли вся карточка (актуально при collapsible — панель истории этапа 1).
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const isFootball = rankingMode === 'FOOTBALL';

  const active = [...players].filter(p => p.status === 'ACTIVE');

  // Медали и места показываем только после завершения хотя бы одного раунда
  const showRanks = hasCompletedRound;

  // Порядок строк берём КАК ЕСТЬ с бэка: список уже отсортирован единым компаратором
  // (StandingsCalculator) — по метрике rankingMode, до первого сыгранного матча — по
  // стартовому посеву (seed), для Beat the Box — с разбивкой по ярусам/боксам. Свою
  // сортировку убрали, чтобы порядок строк и rank всегда совпадали с бэком (иначе при
  // равных победах тай-брейк FOOTBALL расходился: фронт очки→разница, бэк разница→очки).

  // Ярусный/боксовый режим: активен когда хотя бы один игрок имеет groupLabel
  const isTiered = active.some(p => p.groupLabel);
  // Порядок секций = порядок первого появления лейбла в ответе бэка (уже отсортирован).
  const tierRank = {};
  players.forEach(p => {
    if (p.groupLabel && !(p.groupLabel in tierRank)) {
      tierRank[p.groupLabel] = Object.keys(tierRank).length;
    }
  });
  // Этап 2 (ярусы) — если есть Gold/Silver/…; иначе секции-боксы этапа 1.
  const isStage2Tier = active.some(p => !!BTB_TIER_COLORS[p.groupLabel]);
  const boxMode = isTiered && !isStage2Tier;

  const sectionColorOf = (label) =>
    BTB_TIER_COLORS[label] || BTB_BOX_COLORS[(tierRank[label] ?? 0) % BTB_BOX_COLORS.length];

  // Строки в порядке, пришедшем с бэка (только активные игроки).
  const sorted = active;

  // Место внутри секции (1..N) — для этапа 1 показываем именно его (место в боксе),
  // а не сквозной номер по всей таблице. Секции идут подряд, поэтому просто считаем.
  const sectionPlaces = [];
  {
    let curLabel = null;
    let n = 0;
    sorted.forEach(p => {
      if (p.groupLabel !== curLabel) { curLabel = p.groupLabel; n = 0; }
      sectionPlaces.push(++n);
    });
  }

  // Главная (сортировочная) метрика — её показываем в строке всегда
  const primaryValue = (p) => (isFootball ? (p.leaguePoints ?? 0) : (p.totalPoints ?? 0));
  const primaryLabel = isFootball ? 'Очки тур.' : 'Очки';

  // Место: в режиме боксов (этап 1) — место внутри бокса; иначе истинный rank с бэка
  // (standard competition ranking: равные делят место → 1, 2, 2, 4).
  const placeOf = (p, idx) => (boxMode ? sectionPlaces[idx] : (p.rank ?? (idx + 1)));

  // Есть ли повторы среди мест (только для плоской/ярусной таблицы — в боксах место
  // всегда 1..4 без коллизий столбца). Второй столбец «Место» нужен лишь при дублях.
  const ranks = sorted.map(p => p.rank);
  const hasTies = !boxMode
    && ranks.every(r => r != null)
    && new Set(ranks).size !== ranks.length;

  // Медаль выдаём по истинному месту (при дублях двое первых — оба 🥇).
  const medalOf = (place) => (place === 1 ? '🥇' : place === 2 ? '🥈' : place === 3 ? '🥉' : null);

  const COLS = hasTies ? '32px 1fr 48px 16px' : '32px 1fr 52px 16px';
  const headers = hasTies
    ? ['Место', 'Игрок', primaryLabel, '']
    : ['', 'Игрок', primaryLabel, ''];
  const primaryColIdx = 2; // индекс колонки с метрикой (для выравнивания)

  const allOpen = sorted.length > 0 && sorted.every(p => openIds.has(p.id));

  const toggleRow = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setOpenIds(allOpen ? new Set() : new Set(sorted.map(p => p.id)));
  };

  // Пояснение о подсчёте мест — зависит от режима.
  const footerHint = boxMode
    ? 'Этап 1: таблица разбита по боксам, места считаются внутри каждого бокса. Топ-2 каждого бокса поднимаются в старшие группы этапа 2.'
    : isTiered
      ? 'Beat the Box: этап 2 разбит по группам (Gold → Silver → Bronze → Crystal). Победитель турнира — №1 Gold. Очки — только за этап 2.'
      : isFootball
        ? 'Места: число побед (победа 1, ничья и поражение 0) → разница очков → сумма очков → меньше поражений.'
        : 'Места: сумма очков → разница очков → больше побед → меньше поражений.';

  // Кнопка в шапке: при collapsible — свернуть/развернуть всю карточку,
  // иначе — прежний тумблер «Подробно» (раскрыть все строки).
  const headerAction = collapsible ? (
    <button
      onClick={() => setCollapsed(c => !c)}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        border: 'none', background: 'transparent', cursor: 'pointer',
        padding: 0, fontSize: 11, fontWeight: 700, color: '#6b7280',
      }}
    >
      {collapsed ? 'Развернуть' : 'Свернуть'}
      <span style={{
        fontSize: 9, transform: collapsed ? 'none' : 'rotate(180deg)',
        transition: 'transform 0.15s',
      }}>
        ▼
      </span>
    </button>
  ) : (
    sorted.length > 0 && (
      <button
        onClick={toggleAll}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          border: 'none', background: 'transparent', cursor: 'pointer',
          padding: 0, fontSize: 11, fontWeight: 700, color: '#d97706',
          textTransform: 'none', letterSpacing: 0,
        }}
      >
        {allOpen ? 'Свернуть' : 'Подробно'}
        <span style={{
          fontSize: 9, transform: allOpen ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.15s',
        }}>
          ▼
        </span>
      </button>
    )
  );

  return (
    <SectionCard title={title} action={headerAction}>
      {collapsible && collapsed ? null : (
        sorted.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '4px 0' }}>
            Нет данных
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Шапка */}
            <div style={{
              display: 'grid', gridTemplateColumns: COLS,
              gap: 6, padding: '0 4px 6px',
              borderBottom: '1px solid #f0f0f5', marginBottom: 4,
            }}>
              {headers.map((h, i) => (
                <span key={i} style={{
                  fontSize: 10, fontWeight: 700, color: '#9ca3af',
                  letterSpacing: 0.5, textTransform: 'uppercase',
                  textAlign: i === primaryColIdx ? 'center' : 'left',
                }}>
                  {h}
                </span>
              ))}
            </div>

            {sorted.map((p, idx) => {
              const place = placeOf(p, idx);
              const medal = medalOf(place);
              const isOpen = openIds.has(p.id);

              // Секционная таблица BtB: заголовок группы/бокса перед первым игроком секции
              const tierColors = isTiered ? sectionColorOf(p.groupLabel) : null;
              const showTierHeader = isTiered && (idx === 0 || sorted[idx - 1].groupLabel !== p.groupLabel);
              // Медаль/подсветка. Beat the Box (isTiered): только №1 Gold — победитель
              // турнира (места 2–3 медалью не выделяем). На этапе 1 (боксы) победителей
              // ещё нет — медалей нет. Прочие форматы — обычный подиум 1–3.
              const isPodium = boxMode
                ? false
                : (isTiered ? (p.groupLabel === 'Gold' && place === 1) : (place <= 3));

              return (
                <div key={p.id}>
                  {/* Заголовок секции (Бокс N на этапе 1 / Gold…Crystal на этапе 2) */}
                  {showTierHeader && p.groupLabel && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 4px 4px',
                      marginTop: idx > 0 ? 8 : 0,
                      borderTop: idx > 0 ? '1px solid #f0f0f5' : 'none',
                    }}>
                      <span style={{
                        fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                        padding: '2px 8px', borderRadius: 99,
                        background: tierColors?.badge || '#f3f4f6',
                        border: `1px solid ${tierColors?.border || '#e5e7eb'}`,
                        color: tierColors?.color || '#374151',
                      }}>
                        {p.groupLabel}
                      </span>
                    </div>
                  )}

                  {/* Строка игрока */}
                  <div style={{
                    borderRadius: 8,
                    background: isOpen
                      ? (tierColors ? tierColors.bg : '#fafafa')
                      : (isPodium ? '#fffbeb' : 'transparent'),
                  }}>
                    {/* Строка целиком кликабельна */}
                    <div
                      onClick={() => toggleRow(p.id)}
                      style={{
                        display: 'grid', gridTemplateColumns: COLS,
                        gap: 6, padding: '8px 4px', alignItems: 'center', cursor: 'pointer',
                      }}
                    >
                      {/* Место: до начала игр — сквозная нумерация, после — место (в боксе/rank) */}
                      <span style={{ fontSize: showRanks && medal && isPodium ? 22 : 13, fontWeight: 700, color: showRanks && isPodium ? '#d97706' : '#9ca3af', lineHeight: 1 }}>
                        {showRanks ? ((medal && isPodium) ? medal : place) : (idx + 1)}
                      </span>
                      <span style={{ minWidth: 0, overflow: 'hidden' }}>
                        <MemberInline member={p} fontSize={13} showNick />
                      </span>
                      <span style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', lineHeight: 1.1,
                      }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
                          {primaryValue(p)}
                        </span>
                        {showRanks && tieBreakHint(p) && (
                          <span style={{
                            fontSize: 8.5, fontWeight: 700, color: '#9ca3af',
                            whiteSpace: 'nowrap', marginTop: 1,
                          }}>
                            {tieBreakHint(p)}
                          </span>
                        )}
                      </span>
                      <span style={{
                        fontSize: 10, color: '#9ca3af', textAlign: 'center',
                        transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
                      }}>
                        ▼
                      </span>
                    </div>

                    {/* Подробная статистика игрока. Колонка «Тур.очки» полезна только
                        в режиме «По победам» — в режиме «Сумма очков» её не показываем. */}
                    {isOpen && (
                      <div style={{ display: 'flex', gap: 5, padding: '0 4px 8px' }}>
                        <StatCell label="Матчи" value={p.matchesPlayed ?? 0} />
                        <StatCell label="В-П-Н" value={`${p.wins ?? 0}-${p.losses ?? 0}-${p.draws ?? 0}`} />
                        <StatCell label="Разн." value={fmtDiff(p.pointDiff)} />
                        <StatCell label="Очки" value={p.totalPoints ?? 0} accent={!isFootball} />
                        {isFootball && (
                          <StatCell label="Тур.очки" value={p.leaguePoints ?? 0} accent />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Пояснение, как считаются места */}
            <div style={{
              fontSize: 11, color: '#9ca3af', lineHeight: 1.4,
              padding: '7px 4px 0', borderTop: '1px solid #f0f0f5', marginTop: 4,
            }}>
              {footerHint}
              {!isTiered && hasTies && ' Игроки с одинаковыми показателями делят место.'}
            </div>
          </div>
        )
      )}
    </SectionCard>
  );
};

export default Leaderboard;
